import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Polyline, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  TextField,
  Alert,
  Paper
} from '@mui/material';
import {
  Route as RouteIcon,
  Map as MapIcon,
  Assignment as AssignmentIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { BACKEND_BASE_URL } from '../config';
import DocumentUploadCard from './DocumentUploadCard';
import useResponsive from '../hooks/useResponsive';

// Utility function to format dates in Indian format (DD/MM/YYYY)
const formatIndianDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function KPICard({ title, value, icon, color = '#F79233', height = '182px' }) {
  const { isSmallMobile, isMobile } = useResponsive();

  // Responsive height calculation
  const getResponsiveHeight = () => {
    if (isSmallMobile) return Math.min(parseInt(height), 120) + 'px';
    if (isMobile) return Math.min(parseInt(height), 150) + 'px';
    return height;
  };

  return (
    <Card sx={{
      height: getResponsiveHeight(),
      boxShadow: 2,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    }}>
      <CardContent sx={{ p: isSmallMobile ? 1 : isMobile ? 1.5 : 2 }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          mb: isSmallMobile ? 1 : 2,
          flexWrap: isMobile ? 'wrap' : 'nowrap'
        }}>
          {icon && (
            <Box sx={{ mr: isSmallMobile ? 0.5 : 1, flexShrink: 0 }}>
              {React.cloneElement(icon, {
                sx: {
                  fontSize: isSmallMobile ? '1.2rem' : '1.5rem',
                  color: color
                }
              })}
            </Box>
          )}
          <Typography
            variant={isSmallMobile ? "body1" : "h6"}
            component="div"
            sx={{
              ml: isSmallMobile ? 0.5 : 1,
              fontSize: isSmallMobile ? '0.875rem' : isMobile ? '1rem' : '1.1rem',
              wordBreak: 'break-word',
              hyphens: 'auto'
            }}
            className="responsive-text"
          >
            {title}
          </Typography>
        </Box>
        <Typography
          variant={isSmallMobile ? "h6" : isMobile ? "h5" : "h4"}
          component="div"
          sx={{
            color,
            textAlign: 'center',
            fontSize: isSmallMobile ? '1.25rem' : isMobile ? '1.5rem' : '1.75rem',
            fontWeight: 'bold',
            wordBreak: 'break-word'
          }}
          className="responsive-text"
        >
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}



function FieldOfficerCorridorDashboard({ corridor, project }) {
  const [routePath, setRoutePath] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scopeItems, setScopeItems] = useState([]);
  const { isSmallMobile, isMobile, getContainerPadding, getGridBreakpoints } = useResponsive();

  useEffect(() => {
    if (corridor) {
      const items = [];
      if (corridor.scope_of_work_1 && corridor.quantity_1) {
        items.push({
          product_type: corridor.scope_of_work_1,
          quantity: corridor.quantity_1
        });
      }
      if (corridor.scope_of_work_2 && corridor.quantity_2) {
        items.push({
          product_type: corridor.scope_of_work_2,
          quantity: corridor.quantity_2
        });
      }
      if (corridor.scope_of_work_3 && corridor.quantity_3) {
        items.push({
          product_type: corridor.scope_of_work_3,
          quantity: corridor.quantity_3
        });
      }
      setScopeItems(items); // This will set empty array if no scope items
    } else {
      setScopeItems([]); // Reset when no corridor
    }
  }, [corridor]);

  const [workStatus, setWorkStatus] = useState({
    site_visit: {
      date: '',
      documents: {
        site_visit_report: [],
        photos_with_geolocation: []
      }
    },
    design_approval: {
      date: '',
      documents: {
        approved_designs_pdf: [],
        photos: []
      }
    },
    product_delivery: {
      date: '',
      documents: {
        eway_bill: [],
        delivery_photos: [],
        proforma_invoice: [],
        acknowledged_grn: []
      }
    },
    installation: {
      date: '',
      documents: {
        installation_photos: [],
        handover_report: []
      }
    }
  });

  const [projectDocumentRequirements, setProjectDocumentRequirements] = useState({});
  const [saveStatus, setSaveStatus] = useState({ show: false, type: '', message: '' });
  const mapRef = useRef(null);

  const center = [
    (parseFloat(corridor.from_lat) + parseFloat(corridor.to_lat)) / 2,
    (parseFloat(corridor.from_lng) + parseFloat(corridor.to_lng)) / 2
  ];

  useEffect(() => {
    fetchRoute();
    fetchWorkStatus();
    if (project?.document_requirements) {
      setProjectDocumentRequirements(project.document_requirements);
    }
  }, [corridor, project]);

  // Additional useEffect to handle map focusing when route path changes
  useEffect(() => {
    if (routePath.length > 0 && mapRef.current) {
      fitMapToRoute();
    }
  }, [routePath]);

  const fitMapToRoute = () => {
    if (!mapRef.current || routePath.length === 0) return;

    const map = mapRef.current;
    setTimeout(() => {
      try {
        const bounds = routePath.reduce((bounds, coord) => {
          return bounds.extend(coord);
        }, L.latLngBounds(routePath));

        map.fitBounds(bounds, {
          padding: [50, 50], // Add some padding around the bounds
          maxZoom: 15 // Limit maximum zoom level
        });
      } catch (error) {
        console.error('Error fitting map bounds:', error);
      }
    }, 300); // Increased timeout for map recreation
  };

  const fetchRoute = async () => {
    try {
      setLoading(true);
      // Clear previous route path to ensure fresh data
      setRoutePath([]);

      // Use OSRM API to get the actual road route
      const response = await axios.get(
        `https://router.project-osrm.org/route/v1/driving/${corridor.from_lng},${corridor.from_lat};${corridor.to_lng},${corridor.to_lat}?overview=full&geometries=geojson`
      );

      if (response.data.routes && response.data.routes[0]) {
        // Convert coordinates from [lng, lat] to [lat, lng] format for Leaflet
        const coordinates = response.data.routes[0].geometry.coordinates.map(coord => [coord[1], coord[0]]);
        setRoutePath(coordinates);
      }
    } catch (error) {
      console.error('Error fetching route:', error);
      // Fallback to straight line if route fetching fails
      const fallbackRoute = [
        [parseFloat(corridor.from_lat), parseFloat(corridor.from_lng)],
        [parseFloat(corridor.to_lat), parseFloat(corridor.to_lng)]
      ];
      setRoutePath(fallbackRoute);
    } finally {
      setLoading(false);
    }
  };



  const fetchWorkStatus = async () => {
    try {
      if (!corridor.corridor_id || !corridor.project_id) return;

      const encodedCorridorId = encodeURIComponent(corridor.corridor_id);
      const response = await axios.get(`${BACKEND_BASE_URL}/api/field-officer/work-status/${corridor.project_id}/${encodedCorridorId}`);
      if (response.data) {
        // Convert dates to YYYY-MM-DD format for the date input and handle documents
        const formattedData = {
          site_visit: {
            date: response.data.site_visit?.date ? new Date(response.data.site_visit.date).toISOString().split('T')[0] : '',
            documents: {
              site_visit_report: response.data.site_visit?.documents?.site_visit_report || [],
              photos_with_geolocation: response.data.site_visit?.documents?.photos_with_geolocation || []
            }
          },
          design_approval: {
            date: response.data.design_approval?.date ? new Date(response.data.design_approval.date).toISOString().split('T')[0] : '',
            documents: {
              approved_designs_pdf: response.data.design_approval?.documents?.approved_designs_pdf || [],
              photos: response.data.design_approval?.documents?.photos || []
            }
          },
          product_delivery: {
            date: response.data.product_delivery?.date ? new Date(response.data.product_delivery.date).toISOString().split('T')[0] : '',
            documents: {
              eway_bill: response.data.product_delivery?.documents?.eway_bill || [],
              delivery_photos: response.data.product_delivery?.documents?.delivery_photos || [],
              proforma_invoice: response.data.product_delivery?.documents?.proforma_invoice || [],
              acknowledged_grn: response.data.product_delivery?.documents?.acknowledged_grn || []
            }
          },
          installation: {
            date: response.data.installation?.date ? new Date(response.data.installation.date).toISOString().split('T')[0] : '',
            documents: {
              installation_photos: response.data.installation?.documents?.installation_photos || [],
              handover_report: response.data.installation?.documents?.handover_report || []
            }
          }
        };
        setWorkStatus(formattedData);
      }
    } catch (error) {
      console.error('Error fetching work status:', error);
      // Reset work status on error with new structure
      setWorkStatus({
        site_visit: {
          date: '',
          documents: {
            site_visit_report: [],
            photos_with_geolocation: []
          }
        },
        design_approval: {
          date: '',
          documents: {
            approved_designs_pdf: [],
            photos: []
          }
        },
        product_delivery: {
          date: '',
          documents: {
            eway_bill: [],
            delivery_photos: [],
            proforma_invoice: [],
            acknowledged_grn: []
          }
        },
        installation: {
          date: '',
          documents: {
            installation_photos: [],
            handover_report: []
          }
        }
      });
    }
  };

  const handleDateChange = (feature) => (newDate) => {
    setWorkStatus(prev => ({
      ...prev,
      [feature]: { ...prev[feature], date: newDate }
    }));
  };

  const handleDocumentChange = (stage, documentType) => (newDocuments) => {
    setWorkStatus(prev => ({
      ...prev,
      [stage]: {
        ...prev[stage],
        documents: {
          ...prev[stage].documents,
          [documentType]: newDocuments
        }
      }
    }));
  };

  const handleDocumentDelete = (stage) => async (filePath) => {
    try {
      const encodedCorridorId = encodeURIComponent(corridor.corridor_id);
      const response = await axios.post(
        `${BACKEND_BASE_URL}/api/field-officer/work-status/${corridor.project_id}/${encodedCorridorId}/delete-image`,
        {
          stage: stage,
          image_path: filePath
        }
      );

      if (response.status === 200) {
        console.log('Document deleted successfully');
      } else {
        throw new Error('Failed to delete document');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error; // Re-throw to prevent UI update if deletion failed
    }
  };

  const handleSave = async () => {
    try {
      if (!corridor.corridor_id || !corridor.project_id) {
        setSaveStatus({
          show: true,
          type: 'error',
          message: 'Invalid corridor or project ID'
        });
        return;
      }

      // Create FormData for file uploads
      const formData = new FormData();

      // Add dates to form data
      const stages = ['site_visit', 'design_approval', 'product_delivery', 'installation'];

      stages.forEach(stage => {
        if (workStatus[stage].date) {
          formData.append(`${stage}_date`, workStatus[stage].date);
        }

        // Add documents to form data based on stage
        const stageDocuments = workStatus[stage].documents;

        Object.keys(stageDocuments).forEach(documentType => {
          const documents = stageDocuments[documentType];
          documents.forEach((doc) => {
            if (doc instanceof File) {
              // New file to upload
              formData.append(documentType, doc);
            }
            // Existing documents (file paths) are handled by the backend
          });
        });
      });

      const encodedCorridorId = encodeURIComponent(corridor.corridor_id);
      await axios.post(
        `${BACKEND_BASE_URL}/api/field-officer/work-status/${corridor.project_id}/${encodedCorridorId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setSaveStatus({
        show: true,
        type: 'success',
        message: 'Work status updated successfully'
      });

      // Refresh work status to get updated file paths
      await fetchWorkStatus();

    } catch (error) {
      setSaveStatus({
        show: true,
        type: 'error',
        message: error.response?.data?.error || 'Failed to update work status'
      });
    }
    setTimeout(() => setSaveStatus({ show: false, type: '', message: '' }), 3000);
  };

  useEffect(() => {
    fetchWorkStatus();
  }, [corridor]);

  return (
    <Box sx={{
      p: getContainerPadding(),
      height: 'calc(100vh - 80px)',
      overflow: 'auto'
    }}>
      <Grid container spacing={isSmallMobile ? 1 : 2}>
        {/* Map Section */}
        <Grid item xs={12} md={8}>
          <Card sx={{
            height: isSmallMobile ? '250px' : isMobile ? '300px' : '370px',
            mb: isSmallMobile ? 0.5 : 1,
            overflow: 'hidden'
          }}>
            <CardContent sx={{ height: '100%', p: 0, position: 'relative' }}>
              {loading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    zIndex: 1000
                  }}
                >
                  <Typography
                    variant={isSmallMobile ? "body2" : "body1"}
                    className="responsive-text"
                  >
                    Loading route...
                  </Typography>
                </Box>
              )}
              <MapContainer
                key={`map-${corridor.corridor_id}-${corridor.from_lat}-${corridor.from_lng}`}
                center={center}
                zoom={isMobile ? 9 : 10}
                style={{
                  height: '100%',
                  width: '100%',
                  touchAction: 'pan-x pan-y'
                }}
                className="map-container"
                zoomControl={!isSmallMobile}
                attributionControl={false}
                whenCreated={(mapInstance) => {
                  mapRef.current = mapInstance;

                  // Add mobile-friendly zoom control
                  if (isSmallMobile) {
                    const zoomControl = L.control.zoom({
                      position: 'topright'
                    });
                    zoomControl.addTo(mapInstance);
                  }

                  // Fit bounds immediately when map is created
                  if (routePath.length > 0) {
                    setTimeout(() => {
                      try {
                        const bounds = routePath.reduce((bounds, coord) => {
                          return bounds.extend(coord);
                        }, L.latLngBounds(routePath));
                        mapInstance.fitBounds(bounds, {
                          padding: isSmallMobile ? [20, 20] : [50, 50],
                          maxZoom: isMobile ? 14 : 15
                        });
                      } catch (error) {
                        console.error('Error fitting map bounds on creation:', error);
                      }
                    }, 100);
                  }
                }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution=""
                />
                {routePath.length > 0 && (
                  <Polyline
                    positions={routePath}
                    color="#F79233"
                    weight={4}
                    opacity={0.8}
                    smoothFactor={1}
                  />
                )}
                <Marker position={[parseFloat(corridor.from_lat), parseFloat(corridor.from_lng)]}>
                  <Popup>
                    <Typography variant="subtitle2">Start Point</Typography>
                    <Typography variant="body2">Corridor: {corridor.corridor}</Typography>
                  </Popup>
                </Marker>
                <Marker position={[parseFloat(corridor.to_lat), parseFloat(corridor.to_lng)]}>
                  <Popup>
                    <Typography variant="subtitle2">End Point</Typography>
                    <Typography variant="body2">Corridor: {corridor.corridor}</Typography>
                  </Popup>
                </Marker>
              </MapContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* KPIs Section */}
        <Grid item xs={12} md={4}>
          <Grid container spacing={isSmallMobile ? 1 : 2}>
            <Grid item xs={isSmallMobile ? 12 : 6}>
              <KPICard
                title="Corridor"
                value={corridor.corridor}
                icon={<RouteIcon sx={{ color: '#F79233' }} />}
                height={isSmallMobile ? "100px" : isMobile ? "140px" : "170px"}
              />
            </Grid>
            <Grid item xs={isSmallMobile ? 12 : 6}>
              <KPICard
                title="Stretch"
                value={`${corridor.stretch_km} KM`}
                icon={<MapIcon sx={{ color: '#F79233' }} />}
                height={isSmallMobile ? "100px" : isMobile ? "140px" : "170px"}
              />
            </Grid>
            <Grid item xs={12}>
              <Card sx={{ minHeight: isSmallMobile ? '150px' : '200px' }}>
                <CardContent sx={{ p: isSmallMobile ? 1 : 2 }}>
                  <Typography
                    variant={isSmallMobile ? "body1" : "subtitle1"}
                    sx={{
                      mb: 1,
                      color: '#F79233',
                      fontSize: isSmallMobile ? '0.875rem' : '1rem'
                    }}
                  >
                    Scope of Work
                  </Typography>
                  <Grid container spacing={isSmallMobile ? 0.5 : 1}>
                    {scopeItems.length > 0 ? (
                      scopeItems.map((item, index) => (
                        <Grid item xs={isSmallMobile ? 12 : 6} key={index}>
                          <KPICard
                            title={item.product_type}
                            value={item.quantity}
                            icon={<AssignmentIcon sx={{ color: '#F79233' }} />}
                            height={isSmallMobile ? "80px" : "100px"}
                          />
                        </Grid>
                      ))
                    ) : (
                      <Grid item xs={12}>
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: isSmallMobile ? '80px' : '100px',
                          color: '#666',
                          fontStyle: 'italic',
                          textAlign: 'center',
                          p: 1
                        }}>
                          <Typography
                            variant={isSmallMobile ? "caption" : "body2"}
                            className="responsive-text"
                          >
                            No scope of work defined for this corridor
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Work Status Update Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: isSmallMobile ? 1 : 2 }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: isSmallMobile ? 'flex-start' : 'center',
              mb: isSmallMobile ? 1 : 2,
              flexDirection: isSmallMobile ? 'column' : 'row',
              gap: isSmallMobile ? 1 : 0
            }}>
              <Typography
                variant={isSmallMobile ? "subtitle1" : "h6"}
                sx={{
                  color: '#F79233',
                  fontSize: isSmallMobile ? '1rem' : '1.25rem',
                  mb: isSmallMobile ? 1 : 0
                }}
                className="responsive-text"
              >
                Field Officer Work Update
              </Typography>
              <Button
                variant="contained"
                startIcon={!isSmallMobile && <SaveIcon />}
                onClick={handleSave}
                fullWidth={isSmallMobile}
                size={isSmallMobile ? "medium" : "small"}
                sx={{
                  bgcolor: '#F79233',
                  '&:hover': { bgcolor: '#E67E22' },
                  fontSize: isSmallMobile ? '0.875rem' : '1rem'
                }}
              >
                Save Updates
              </Button>
            </Box>

            {saveStatus.show && (
              <Alert severity={saveStatus.type} sx={{ mb: 2 }}>
                {saveStatus.message}
              </Alert>
            )}

            {/* Site Visit Stage - Show only if at least one subsection is enabled */}
            {projectDocumentRequirements.site_visit &&
             Object.values(projectDocumentRequirements.site_visit).some(Boolean) && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: '#F79233', flexGrow: 1 }}>
                    Site Visit
                  </Typography>
                  {project?.stage_deadlines?.site_visit && (
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      Deadline: {formatIndianDate(project.stage_deadlines.site_visit)}
                    </Typography>
                  )}
                </Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      type="date"
                      label="Site Visit Date"
                      value={workStatus.site_visit.date || ''}
                      onChange={(e) => handleDateChange('site_visit')(e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: new Date().toISOString().split('T')[0] }}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  {/* Site Visit Report - Show only if enabled */}
                  {projectDocumentRequirements.site_visit?.site_visit_report && (
                    <Grid item xs={12} md={6}>
                      <DocumentUploadCard
                        documentType="site_visit_report"
                        documents={workStatus.site_visit.documents.site_visit_report}
                        onDocumentChange={handleDocumentChange('site_visit', 'site_visit_report')}
                        onDocumentDelete={handleDocumentDelete('site_visit')}
                        isRequired={projectDocumentRequirements.site_visit?.site_visit_report}
                        allowedTypes={['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png']}
                      />
                    </Grid>
                  )}

                  {/* Photos with Geolocation - Show only if enabled */}
                  {projectDocumentRequirements.site_visit?.photos_with_geolocation && (
                    <Grid item xs={12} md={6}>
                      <DocumentUploadCard
                        documentType="photos_with_geolocation"
                        documents={workStatus.site_visit.documents.photos_with_geolocation}
                        onDocumentChange={handleDocumentChange('site_visit', 'photos_with_geolocation')}
                        onDocumentDelete={handleDocumentDelete('site_visit')}
                        isRequired={projectDocumentRequirements.site_visit?.photos_with_geolocation}
                        allowedTypes={['jpg', 'jpeg', 'png']}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            {/* Design Approval Stage - Show only if at least one subsection is enabled */}
            {projectDocumentRequirements.design_approval &&
             Object.values(projectDocumentRequirements.design_approval).some(Boolean) && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: '#F79233', flexGrow: 1 }}>
                    Design Approval
                  </Typography>
                  {project?.stage_deadlines?.design_approval && (
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      Deadline: {formatIndianDate(project.stage_deadlines.design_approval)}
                    </Typography>
                  )}
                </Box>
                <Grid container spacing={isSmallMobile ? 1 : 2} sx={{ mb: isSmallMobile ? 1 : 2 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      type="date"
                      label="Design Approval Date"
                      value={workStatus.design_approval.date || ''}
                      onChange={(e) => handleDateChange('design_approval')(e.target.value)}
                      fullWidth
                      size={isSmallMobile ? "small" : "small"}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: new Date().toISOString().split('T')[0] }}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: isSmallMobile ? '0.875rem' : '1rem'
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: isSmallMobile ? '0.875rem' : '1rem'
                        }
                      }}
                      className="touch-target"
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  {/* Approved Designs PDF - Show only if enabled */}
                  {projectDocumentRequirements.design_approval?.approved_designs_pdf && (
                    <Grid item xs={12} md={6}>
                      <DocumentUploadCard
                        documentType="approved_designs_pdf"
                        documents={workStatus.design_approval.documents.approved_designs_pdf}
                        onDocumentChange={handleDocumentChange('design_approval', 'approved_designs_pdf')}
                        onDocumentDelete={handleDocumentDelete('design_approval')}
                        isRequired={projectDocumentRequirements.design_approval?.approved_designs_pdf}
                        allowedTypes={['pdf']}
                      />
                    </Grid>
                  )}

                  {/* Photos - Show only if enabled */}
                  {projectDocumentRequirements.design_approval?.photos && (
                    <Grid item xs={12} md={6}>
                      <DocumentUploadCard
                        documentType="photos"
                        documents={workStatus.design_approval.documents.photos}
                        onDocumentChange={handleDocumentChange('design_approval', 'photos')}
                        onDocumentDelete={handleDocumentDelete('design_approval')}
                        isRequired={projectDocumentRequirements.design_approval?.photos}
                        allowedTypes={['jpg', 'jpeg', 'png']}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            {/* Product Delivery Stage - Show only if at least one subsection is enabled */}
            {projectDocumentRequirements.product_delivery &&
             Object.values(projectDocumentRequirements.product_delivery).some(Boolean) && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: '#F79233', flexGrow: 1 }}>
                    Product Delivery
                  </Typography>
                  {project?.stage_deadlines?.product_delivery && (
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      Deadline: {formatIndianDate(project.stage_deadlines.product_delivery)}
                    </Typography>
                  )}
                </Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      type="date"
                      label="Product Delivery Date"
                      value={workStatus.product_delivery.date || ''}
                      onChange={(e) => handleDateChange('product_delivery')(e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: new Date().toISOString().split('T')[0] }}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  {/* E-way Bill - Show only if enabled */}
                  {projectDocumentRequirements.product_delivery?.eway_bill && (
                    <Grid item xs={12} md={6}>
                      <DocumentUploadCard
                        documentType="eway_bill"
                        documents={workStatus.product_delivery.documents.eway_bill}
                        onDocumentChange={handleDocumentChange('product_delivery', 'eway_bill')}
                        onDocumentDelete={handleDocumentDelete('product_delivery')}
                        isRequired={projectDocumentRequirements.product_delivery?.eway_bill}
                        allowedTypes={['pdf', 'jpg', 'jpeg', 'png']}
                      />
                    </Grid>
                  )}

                  {/* Delivery Photos - Show only if enabled */}
                  {projectDocumentRequirements.product_delivery?.delivery_photos && (
                    <Grid item xs={12} md={6}>
                      <DocumentUploadCard
                        documentType="delivery_photos"
                        documents={workStatus.product_delivery.documents.delivery_photos}
                        onDocumentChange={handleDocumentChange('product_delivery', 'delivery_photos')}
                        onDocumentDelete={handleDocumentDelete('product_delivery')}
                        isRequired={projectDocumentRequirements.product_delivery?.delivery_photos}
                        allowedTypes={['jpg', 'jpeg', 'png']}
                      />
                    </Grid>
                  )}

                  {/* Proforma Invoice - Show only if enabled */}
                  {projectDocumentRequirements.product_delivery?.proforma_invoice && (
                    <Grid item xs={12} md={6}>
                      <DocumentUploadCard
                        documentType="proforma_invoice"
                        documents={workStatus.product_delivery.documents.proforma_invoice}
                        onDocumentChange={handleDocumentChange('product_delivery', 'proforma_invoice')}
                        onDocumentDelete={handleDocumentDelete('product_delivery')}
                        isRequired={projectDocumentRequirements.product_delivery?.proforma_invoice}
                        allowedTypes={['pdf', 'jpg', 'jpeg', 'png']}
                      />
                    </Grid>
                  )}

                  {/* Acknowledged GRN - Show only if enabled */}
                  {projectDocumentRequirements.product_delivery?.acknowledged_grn && (
                    <Grid item xs={12} md={6}>
                      <DocumentUploadCard
                        documentType="acknowledged_grn"
                        documents={workStatus.product_delivery.documents.acknowledged_grn}
                        onDocumentChange={handleDocumentChange('product_delivery', 'acknowledged_grn')}
                        onDocumentDelete={handleDocumentDelete('product_delivery')}
                        isRequired={projectDocumentRequirements.product_delivery?.acknowledged_grn}
                        allowedTypes={['pdf', 'jpg', 'jpeg', 'png']}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            {/* Installation Stage - Show only if at least one subsection is enabled */}
            {projectDocumentRequirements.installation &&
             Object.values(projectDocumentRequirements.installation).some(Boolean) && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: '#F79233', flexGrow: 1 }}>
                    Installation
                  </Typography>
                  {project?.stage_deadlines?.installation && (
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      Deadline: {formatIndianDate(project.stage_deadlines.installation)}
                    </Typography>
                  )}
                </Box>
                <Grid container spacing={isSmallMobile ? 1 : 2} sx={{ mb: isSmallMobile ? 1 : 2 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      type="date"
                      label="Installation Date"
                      value={workStatus.installation.date || ''}
                      onChange={(e) => handleDateChange('installation')(e.target.value)}
                      fullWidth
                      size={isSmallMobile ? "small" : "small"}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: new Date().toISOString().split('T')[0] }}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: isSmallMobile ? '0.875rem' : '1rem'
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: isSmallMobile ? '0.875rem' : '1rem'
                        }
                      }}
                      className="touch-target"
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  {/* Installation Photos - Show only if enabled */}
                  {projectDocumentRequirements.installation?.installation_photos && (
                    <Grid item xs={12} md={6}>
                      <DocumentUploadCard
                        documentType="installation_photos"
                        documents={workStatus.installation.documents.installation_photos}
                        onDocumentChange={handleDocumentChange('installation', 'installation_photos')}
                        onDocumentDelete={handleDocumentDelete('installation')}
                        isRequired={projectDocumentRequirements.installation?.installation_photos}
                        allowedTypes={['jpg', 'jpeg', 'png']}
                      />
                    </Grid>
                  )}

                  {/* Handover Report - Show only if enabled */}
                  {projectDocumentRequirements.installation?.handover_report && (
                    <Grid item xs={12} md={6}>
                      <DocumentUploadCard
                        documentType="handover_report"
                        documents={workStatus.installation.documents.handover_report}
                        onDocumentChange={handleDocumentChange('installation', 'handover_report')}
                        onDocumentDelete={handleDocumentDelete('installation')}
                        isRequired={projectDocumentRequirements.installation?.handover_report}
                        allowedTypes={['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png']}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default FieldOfficerCorridorDashboard; 
